<template>
  <div class="bm-bg" :style="{ height: heightPx }">
    <canvas ref="c" class="bm-bg__canvas"></canvas>

    <div
      v-if="hatch"
      class="bm-bg__hatch"
      :style="{
        opacity: hatchOpacity,
      }"
    ></div>

    <svg
      v-if="circles"
      class="bm-bg__circles"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      :style="{ opacity: circlesOpacity }"
    >
      <!-- Dashed rings (rotate in place) -->
      <g fill="none" :stroke="circlesStroke" stroke-width="0.35" stroke-dasharray="2 2">
        <circle class="bm-bg__circle-rotate-1" cx="82" cy="48" r="18" />
        <circle class="bm-bg__circle-rotate-2" cx="25" cy="80" r="22" />
      </g>
    </svg>

    <!-- Water drops overlay (HTML to avoid SVG cropping) -->
    <div v-if="circles" class="bm-bg__drops" :style="{ opacity: circlesOpacity }" aria-hidden="true">
      <span
        v-for="drop in waterDrops"
        :key="drop.id"
        class="bm-bg__drop"
        :style="{
          left: `${drop.x}%`,
          top: `${drop.y}%`,
          width: `${drop.d}px`,
          height: `${drop.d}px`,
          animationDelay: `${drop.delay}s`,
        }"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "shimmer-preview",
  props: {
    height: { type: Number, default: 440 },
    fullscreen: { type: Boolean, default: false },

    // Legacy fallback colors (used only if CSS var parsing fails)
    c1: { type: String, default: "#7b2cff" },
    c2: { type: String, default: "#1f6bff" },
    c3: { type: String, default: "#ff3bd4" },

    hatch: { type: Boolean, default: true },
    hatchOpacity: { type: Number, default: 0.18 },
    circles: { type: Boolean, default: true },
    circlesOpacity: { type: Number, default: 0.3 },

    maxDpr: { type: Number, default: 2 },

    // Motion
    respectReducedMotion: { type: Boolean, default: false },

    warpStrength: { type: Number, default: 0.6 },
    timeScale: { type: Number, default: 0.15 },
  },
  data() {
    return {
      gl: null,
      prog: null,
      raf: null,
      start: 0,
      uTime: null,
      uRes: null,
      uC1: null,
      uC2: null,
      uC3: null,
      uWarp: null,
      uTimeScale: null,
      reducedMotion: false,
      waterDrops: [],
      bgColors01: null,
    };
  },
  computed: {
    heightPx() {
      return this.fullscreen ? '100vh' : `${this.height}px`;
    },
    circlesStroke() {
      return "rgba(255,255,255,.55)";
    },
    circlesStrokeSoft() {
      return "rgba(255,255,255,.30)";
    },
  },
  watch: {
    c1: "drawOnceIfReduced",
    c2: "drawOnceIfReduced",
    c3: "drawOnceIfReduced",
    warpStrength: "drawOnceIfReduced",
    timeScale: "drawOnceIfReduced",
    height() {
      this.$nextTick(() => this.resize());
      this.drawOnceIfReduced();
    },
  },
  mounted() {
    // SSR guard
    if (typeof window === "undefined") return;

    if (this.respectReducedMotion && window.matchMedia) {
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    this.generateWaterDrops();

    // Load colors from CSS var: --header-bg: linear-gradient(...)
    this.loadBackgroundColorsFromCssVar();

    this.initGL();
    this.resize();
    window.addEventListener("resize", this.onResize, { passive: true });

    if (this.reducedMotion) {
      // Render a single frame
      this.renderFrame(performance.now());
    } else {
      this.start = performance.now();
      this.raf = requestAnimationFrame(this.renderFrame);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onResize);
    }
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;

    // Basic GL cleanup
    try {
      if (this.gl && this.prog) this.gl.deleteProgram(this.prog);
    } catch (e) {
      console.log("Error cleaning up GL:", e);
    }
    this.gl = null;
    this.prog = null;
  },
  methods: {
    onResize() {
      this.resize();
      this.generateWaterDrops();
      this.loadBackgroundColorsFromCssVar();
    },
    extractGradientColors(input) {
      // Supports: #RGB/#RGBA/#RRGGBB/#RRGGBBAA, rgb()/rgba(), hsl()/hsla()
      const re = /#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b|rgba?\([^)]*\)|hsla?\([^)]*\)/g;
      return (String(input || "").match(re) || []).map((s) => s.trim());
    },
    clamp01(v) {
      return Math.min(1, Math.max(0, v));
    },
    mixRgb01(a, b, t) {
      const tt = this.clamp01(t);
      return [
        a[0] + ((b[0] - a[0]) * tt),
        a[1] + ((b[1] - a[1]) * tt),
        a[2] + ((b[2] - a[2]) * tt),
      ];
    },
    hexToRgb01(hex) {
      const h = (hex || "").replace("#", "").trim();
      const s = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
      const v = parseInt(s, 16);
      return [
        ((v >> 16) & 255) / 255,
        ((v >> 8) & 255) / 255,
        (v & 255) / 255,
      ];
    },
    parseColorToRgb01(color) {
      const c = String(color || "").trim();
      if (!c) return null;

      if (c[0] === "#") {
        const raw = c.slice(1);
        const hex = raw.length === 4 ? raw.slice(0, 3) : raw.length === 8 ? raw.slice(0, 6) : raw;
        if (hex.length === 3 || hex.length === 6) return this.hexToRgb01(`#${hex}`);
        return null;
      }

      const m = c.match(/^rgba?\(([^)]+)\)$/i);
      if (m) {
        const parts = m[1]
          .split(",")
          .map((p) => p.trim())
          .slice(0, 3)
          .map((p) => (p.endsWith("%") ? (parseFloat(p) * 255) / 100 : parseFloat(p)));
        if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
          return [parts[0] / 255, parts[1] / 255, parts[2] / 255];
        }
      }

      // hsl/hsla or uncommon formats: not converted here
      return null;
    },
    loadBackgroundColorsFromCssVar() {
      if (typeof window === "undefined") return;

      const el = this.$el;
      const stylesEl = el ? window.getComputedStyle(el) : null;
      const stylesRoot = window.getComputedStyle(document.documentElement);
      const raw = (
        (stylesEl && stylesEl.getPropertyValue("--header-bg")) ||
        stylesRoot.getPropertyValue("--header-bg")
      ).trim();

      if (!raw) {
        this.bgColors01 = null;
        return;
      }

      const stops = this.extractGradientColors(raw);
      const rgbStops = stops.map((s) => this.parseColorToRgb01(s)).filter(Boolean);

      // Need 3 colors (uC1/uC2/uC3)
      if (rgbStops.length >= 3) {
        this.bgColors01 = {
          c1: rgbStops[0],
          c2: rgbStops[1],
          c3: rgbStops[2],
        };
        return;
      }

      if (rgbStops.length === 2) {
        const mid = this.mixRgb01(rgbStops[0], rgbStops[1], 0.5);
        this.bgColors01 = {
          c1: rgbStops[0],
          c2: rgbStops[1],
          c3: mid,
        };
        return;
      }

      if (rgbStops.length === 1) {
        this.bgColors01 = {
          c1: rgbStops[0],
          c2: rgbStops[0],
          c3: rgbStops[0],
        };
        return;
      }

      this.bgColors01 = null;
    },
    compile(gl, type, src) {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const msg = gl.getShaderInfoLog(sh);
        gl.deleteShader(sh);
        throw new Error(msg);
      }
      return sh;
    },
    initGL() {
      const canvas = this.$refs.c;
      const gl = canvas.getContext("webgl", { premultipliedAlpha: false, antialias: true });
      if (!gl) throw new Error("WebGL no disponible en este navegador");

      const vert = `
attribute vec2 p;
varying vec2 vUv;
void main(){
  vUv = (p+1.0)*0.5;
  gl_Position = vec4(p,0.0,1.0);
}`;

      const frag = `
precision highp float;
varying vec2 vUv;
uniform vec2 uRes;
uniform float uTime;
uniform vec3 uC1;
uniform vec3 uC2;
uniform vec3 uC3;
uniform float uWarp;
uniform float uTimeScale;

float hash(vec2 p){
  p = fract(p*vec2(123.34, 456.21));
  p += dot(p, p+45.32);
  return fract(p.x*p.y);
}
float noise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  float a=hash(i);
  float b=hash(i+vec2(1.,0.));
  float c=hash(i+vec2(0.,1.));
  float d=hash(i+vec2(1.,1.));
  vec2 u=f*f*(3.0-2.0*f);
  return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
}
float fbm(vec2 p){
  float v=0.0, a=0.5;
  for(int i=0;i<5;i++){
    v += a*noise(p);
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}

void main(){
  vec2 uv = vUv;
  vec2 p = (uv - 0.5);
  p.x *= uRes.x / uRes.y;

  float t = uTime * uTimeScale;

  float n1 = fbm(p*1.2 + vec2(t, -t*0.7));
  float n2 = fbm(p*2.0 + vec2(-t*0.6, t*0.9));
  vec2 warp = vec2(n1 - 0.5, n2 - 0.5);
  vec2 q = p + warp * uWarp;

  float bandTop = smoothstep(0.25, -0.05, q.y + 0.28 + 0.25*sin(uTime*0.35 + q.x*2.0));
  float bandBot = smoothstep(-0.15, 0.45, q.y - 0.35 + 0.30*sin(uTime*0.30 + q.x*1.5));

  float baseMix = smoothstep(-0.65, 0.65, q.x);
  vec3 base = mix(uC2, uC1, baseMix);

  vec3 top = mix(base, uC1, bandTop);
  vec3 bot = mix(top, mix(uC1, uC3, 0.35), bandBot);

  float fog = fbm(q*1.6 + uTime*0.08);
  bot += (fog-0.5)*0.15;

  gl_FragColor = vec4(bot, 1.0);
}`;

      const prog = gl.createProgram();
      gl.attachShader(prog, this.compile(gl, gl.VERTEX_SHADER, vert));
      gl.attachShader(prog, this.compile(gl, gl.FRAGMENT_SHADER, frag));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(prog));
      }
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
        gl.STATIC_DRAW
      );

      const locP = gl.getAttribLocation(prog, "p");
      gl.enableVertexAttribArray(locP);
      gl.vertexAttribPointer(locP, 2, gl.FLOAT, false, 0, 0);

      this.uRes = gl.getUniformLocation(prog, "uRes");
      this.uTime = gl.getUniformLocation(prog, "uTime");
      this.uC1 = gl.getUniformLocation(prog, "uC1");
      this.uC2 = gl.getUniformLocation(prog, "uC2");
      this.uC3 = gl.getUniformLocation(prog, "uC3");
      this.uWarp = gl.getUniformLocation(prog, "uWarp");
      this.uTimeScale = gl.getUniformLocation(prog, "uTimeScale");

      this.gl = gl;
      this.prog = prog;
    },
    resize() {
      if (!this.gl) return;
      const canvas = this.$refs.c;

      const dpr = Math.min(this.maxDpr, window.devicePixelRatio || 1);
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        this.gl.viewport(0, 0, w, h);
        this.gl.uniform2f(this.uRes, w, h);
      }
    },
    setUniforms(timeSec) {
      const gl = this.gl;
      gl.uniform1f(this.uTime, timeSec);

      const colors = this.bgColors01 || {
        c1: this.hexToRgb01(this.c1),
        c2: this.hexToRgb01(this.c2),
        c3: this.hexToRgb01(this.c3),
      };

      gl.uniform3fv(this.uC1, colors.c1);
      gl.uniform3fv(this.uC2, colors.c2);
      gl.uniform3fv(this.uC3, colors.c3);

      gl.uniform1f(this.uWarp, this.warpStrength);
      gl.uniform1f(this.uTimeScale, this.timeScale);
    },
    renderFrame(now) {
      if (!this.gl) return;
      this.resize();

      const t = this.reducedMotion ? 0 : (now - this.start) / 1000;
      this.setUniforms(t);

      this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);

      if (!this.reducedMotion) {
        this.raf = requestAnimationFrame(this.renderFrame);
      }
    },
    drawOnceIfReduced() {
      if (!this.gl) return;
      if (this.reducedMotion) this.renderFrame(performance.now());
    },
    generateWaterDrops() {
      const numDrops = Math.floor(Math.random() * 5) + 8;
      this.waterDrops = [];

      const host = this.$el;
      const w = host ? host.clientWidth : 0;
      const h = host ? host.clientHeight : 0;
      const minDim = Math.max(1, Math.min(w || 1, h || 1));
      // ~5cm @ 96dpi ≈ 189px. Use a dynamic cap for smaller screens.
      const maxDiameterPx = Math.min(190, Math.floor(minDim * 0.22));
      const minDiameterPx = Math.min(28, Math.floor(maxDiameterPx * 0.35));
      
      for (let i = 0; i < numDrops; i++) {
        // Random position, bias away from the center
        let x = 50;
        let y = 50;
        for (let tries = 0; tries < 10; tries++) {
          const nx = Math.random() * 100;
          const ny = Math.random() * 100;
          const inCenter = nx > 30 && nx < 70 && ny > 30 && ny < 70;
          // 80% outside the center box, 20% allowed in the center
          if (!inCenter || Math.random() < 0.2) {
            x = nx;
            y = ny;
            break;
          }
        }
        
        this.waterDrops.push({
          id: i,
          x,
          y,
          d: Math.floor(minDiameterPx + (Math.random() * (maxDiameterPx - minDiameterPx))),
          delay: Math.random() * 6,
        });
      }
    },
  },
};
</script>

<style scoped>
.bm-bg {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #2b0f5a; /* fallback */
}
.bm-bg__canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.bm-bg__hatch {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.35) 0 2px,
    rgba(255, 255, 255, 0) 2px 16px
  );
  mix-blend-mode: overlay;
}
.bm-bg__circles {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* Dashed rings rotation */
.bm-bg__circle-rotate-1 {
  transform-origin: 82% 48%;
  animation: rotate-self 60s linear infinite;
}

.bm-bg__circle-rotate-2 {
  transform-origin: 25% 80%;
  animation: rotate-self 50s linear infinite reverse;
}

@keyframes rotate-self {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Water drops (absolute HTML) */
.bm-bg__drops {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.bm-bg__drop {
  position: absolute;
  border: 1px solid rgba(255,255,255,.30);
  border-radius: 9999px;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  animation: water-drop 5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  will-change: transform, opacity;
}

.bm-bg__circles-drops .drop-1 {
  animation-delay: 0s;
}

.bm-bg__circles-drops .drop-2 {
  animation-delay: 0.6s;
}

.bm-bg__circles-drops .drop-3 {
  animation-delay: 1.2s;
}

@keyframes water-drop {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  20% {
    transform: translate(-50%, -50%) scale(0.55);
    opacity: 0.4;
  }
  40% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }
  60% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }
  80% {
    transform: translate(-50%, -50%) scale(0.85);
    opacity: 0.3;
  }
  100% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
}
</style>
